import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Check } from 'lucide-react';
import type { ShortcutId } from '@evi/shared';
import { PINNED_SHORTCUTS_COUNT, SHORTCUT_DEFAULTS } from '@evi/shared';
import { usePinnedShortcuts } from '@/hooks/usePinnedShortcuts';
import { SHORTCUT_CATALOG, shortcutsOfferedTo } from '@/lib/shortcuts';
import { toast } from '@/store/toast';
import styles from './ShortcutsPage.module.css';

interface Props { onNavigate: (path: string) => void }

/**
 * /shortcuts — pick the three home-screen pins.
 *
 * Tap to add, tap to remove; the fourth tap on a new one swaps out the
 * oldest so there are never more than three. Saved on the User through
 * PATCH /me/preferences (usePinnedShortcuts), so the choice survives a
 * reinstall and follows the driver to a new phone; "Use defaults"
 * hands the set back to the persona rule (homeowner / fleet / driver).
 * The list offered depends on the persona — a driver with no charger is
 * not offered Earnings — while a pin the driver already holds stays
 * visible even if it no longer matches, so nothing disappears from
 * under them.
 */
export function ShortcutsPage({ onNavigate }: Props) {
  const { t } = useTranslation('shortcuts');
  const { persona, pinnedIds, isCustom, setPins, resetPins } = usePinnedShortcuts();
  const [selected, setSelected] = useState<ShortcutId[]>([...pinnedIds]);
  const [saving, setSaving] = useState(false);

  const offered = shortcutsOfferedTo(persona);
  // Anything currently pinned but not offered to this persona still
  // lists (from the catalog — by construction it is not in `offered`),
  // so a pin that stopped matching can be seen and swapped out.
  const extra = pinnedIds
    .filter((id) => !offered.some((d) => d.id === id))
    .map((id) => SHORTCUT_CATALOG[id]);
  const options = [...offered, ...extra];

  const toggle = (id: ShortcutId) => {
    setSelected((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      const next = [...cur, id];
      return next.length > PINNED_SHORTCUTS_COUNT ? next.slice(next.length - PINNED_SHORTCUTS_COUNT) : next;
    });
  };

  const dirty = selected.length === PINNED_SHORTCUTS_COUNT
    && selected.some((id, i) => id !== pinnedIds[i]);
  const defaults = SHORTCUT_DEFAULTS[persona];

  const save = async () => {
    if (selected.length !== PINNED_SHORTCUTS_COUNT || saving) return;
    setSaving(true);
    try {
      await setPins(selected);
      toast(t('saved'), { tone: 'accent' });
      onNavigate('/profile');
    } catch {
      toast(t('saveError'), { tone: 'error' });
      setSaving(false);
    }
  };

  const useDefaults = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await resetPins();
      setSelected([...defaults]);
      toast(t('saved'), { tone: 'accent' });
      onNavigate('/profile');
    } catch {
      toast(t('saveError'), { tone: 'error' });
      setSaving(false);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label={t('a11y.back')}
          onClick={() => onNavigate('/profile')}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.subtitle}>{t('subtitle', { count: PINNED_SHORTCUTS_COUNT })}</p>
        </div>
      </header>

      {/* The three, in order, as they sit on the home. */}
      <div className={styles.pinRow} aria-label={t('a11y.current')}>
        {Array.from({ length: PINNED_SHORTCUTS_COUNT }).map((_, i) => {
          const id = selected[i];
          const def = id ? SHORTCUT_CATALOG[id] : undefined;
          return (
            <div key={i} className={`${styles.pinSlot} ${def ? styles.pinSlotFilled : ''}`}>
              <span className={styles.pinIcon} aria-hidden>{def?.icon}</span>
              <span className={styles.pinLabel}>{def ? t(def.labelKey) : t('emptySlot')}</span>
            </div>
          );
        })}
      </div>

      <div className={styles.list} role="group" aria-label={t('title')}>
        {options.map((d) => {
          const on = selected.includes(d.id);
          return (
            <button
              key={d.id}
              type="button"
              className={`${styles.option} ${on ? styles.optionOn : ''}`}
              aria-pressed={on}
              onClick={() => toggle(d.id)}
            >
              <span className={styles.optionIcon} aria-hidden>{d.icon}</span>
              <span className={styles.optionLabel}>{t(d.labelKey)}</span>
              <span className={styles.optionCheck} aria-hidden>{on && <Check size={16} />}</span>
            </button>
          );
        })}
      </div>

      {/* In-flow controls — nothing bottom-anchored. */}
      <button
        type="button"
        className={styles.save}
        onClick={() => void save()}
        disabled={!dirty || saving}
      >
        {saving ? t('saving') : t('save')}
      </button>
      <button
        type="button"
        className={styles.defaults}
        onClick={() => void useDefaults()}
        disabled={saving || !isCustom}
      >
        {t('useDefaults', { a: t(`items.${defaults[0]}`), b: t(`items.${defaults[1]}`), c: t(`items.${defaults[2]}`) })}
      </button>
    </div>
  );
}
